import { Badge } from '@/components/ui/Badge'
import {
  CONTACT_ORDER,
  MAKKAH_CONTACTS,
  MAKKAH_TIME_ZONE,
  PRIMARY_SITE,
} from '@/config/eclipse'
import { useI18n } from '@/i18n'
import { formatDuration } from '@/lib/countdown'
import { formatTimeInZone, viewerTimeZone } from '@/lib/datetime'
import { cn } from '@/lib/cn'

/**
 * The five contacts, in Makkah time with the visitor's own zone alongside.
 * Renders as a stacked list on mobile and a bordered table-like grid on wider
 * screens — no horizontal scrolling at any width.
 */
export function ContactTimeline() {
  const { t, locale } = useI18n()
  const localZone = viewerTimeZone()
  const showsLocalColumn = localZone !== MAKKAH_TIME_ZONE

  return (
    <div className="overflow-hidden rounded-2xl border border-gold/12 bg-white/[0.02]">
      <ul className="divide-y divide-line/70">
        {CONTACT_ORDER.map((key) => {
          const iso = MAKKAH_CONTACTS[key]
          const isTotality = key === 'totalityBegin' || key === 'totalityEnd'

          return (
            <li
              key={key}
              className={cn(
                'flex flex-col gap-1 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6',
                isTotality && 'bg-gold/[0.045]',
              )}
            >
              <span
                className={cn(
                  'text-sm sm:text-base',
                  isTotality ? 'text-parchment' : 'text-muted',
                )}
              >
                {t.timeline.contacts[key]}
              </span>

              <span className="flex items-baseline gap-3 sm:gap-5">
                <span className="tabular font-serif text-lg text-gold sm:text-xl">
                  {formatTimeInZone(iso, MAKKAH_TIME_ZONE, locale)}
                </span>
                {showsLocalColumn ? (
                  <span className="tabular text-xs text-faint">
                    {formatTimeInZone(iso, localZone, locale)} · {t.countdown.localTime}
                  </span>
                ) : null}
              </span>
            </li>
          )
        })}
      </ul>

      <div className="flex flex-wrap items-center gap-3 border-t border-line/70 px-4 py-4 sm:px-6">
        <Badge tone="gold">{t.timeline.totalityDuration}</Badge>
        <span className="tabular font-serif text-lg text-parchment">
          {formatDuration(PRIMARY_SITE.totalitySeconds)}
        </span>
        <span className="ml-auto text-[0.6875rem] uppercase tracking-[0.18em] text-faint">
          {t.countdown.makkahTime} · UTC+3
        </span>
      </div>
    </div>
  )
}
